/**
 * 数字资产 · 新建角色/场景 Wizard
 *
 * 3 步:
 *  1) 基本信息(名字 + 维度)
 *  2) 参考图(可上传 / 可选历史 / 可跳过)
 *  3) 出图设置 + 多视图生成(同屏:点开始生成后,设置区只读;5s 轮询任务;完成后选一张锁定)
 *
 * 现有 asset(从 AssetDetailDrawer 进入)可跳过 1/2 直接 step3 重新生成。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  Upload,
  ImagePlus,
  RefreshCw,
  Check,
  Wand2,
  ChevronLeft,
  ChevronRight,
  X,
  Lock,
  Maximize2,
} from 'lucide-react';
import { Lightbox } from './Lightbox';
import {
  AGE_RANGE_OPTIONS,
  CHARACTER_PERSONALITY_OPTIONS,
  CHARACTER_ROLES,
  LOCATION_KIND_OPTIONS,
  PROP_CATEGORY_OPTIONS,
  PROP_MATERIAL_OPTIONS,
  PROP_STYLE_OPTIONS,
  PROP_SIZE_OPTIONS,
  SEEDREAM_QUALITY_OPTIONS,
} from './types';
import { useVideoStyles } from '@/hooks/useVideoStyles';
import type {
  BrainAsset,
  BrainGenTask,
  BrainKind,
  BrainTemplateListResponse,
  CharacterAttributes,
  LocationAttributes,
  PropAttributes,
} from './types';

type WizardKind = Extract<BrainKind, 'character' | 'location' | 'prop'>;

interface Props {
  open: boolean;
  kind: WizardKind;
  /** 进入时已有的资产(用于"再来一轮"流程,会跳过 step 1/2) */
  initialAsset?: BrainAsset | null;
  /** 新建场景下的默认名称(仅在 initialAsset 为空时生效) */
  defaultName?: string;
  onClose: () => void;
  onCreated?: (asset: BrainAsset) => void;
}

type Step = 1 | 2 | 3;

const KIND_LABEL: Record<WizardKind, string> = { character: '角色', location: '场景', prop: '道具' };

export function CreateAssetDialog({ open, kind, initialAsset, defaultName, onClose, onCreated }: Props) {
  const label = KIND_LABEL[kind];
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState('');
  const [charAttrs, setCharAttrs] = useState<CharacterAttributes>({});
  const [locAttrs, setLocAttrs] = useState<LocationAttributes>({});
  const [propAttrs, setPropAttrs] = useState<PropAttributes>({});
  const [refImages, setRefImages] = useState<string[]>([]);
  const [count, setCount] = useState(3);
  const [size, setSize] = useState<string>('2K');
  const [realism, setRealism] = useState<boolean>(false);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [pickedCandidateId, setPickedCandidateId] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // 重置 / 进入已有资产
  useEffect(() => {
    if (!open) return;
    if (initialAsset) {
      setStep(3);
      setName(initialAsset.name);
      setAssetId(initialAsset.assetId);
      setRefImages(initialAsset.referenceImages ?? []);
      if (kind === 'character') setCharAttrs(initialAsset.attributes as CharacterAttributes);
      else if (kind === 'location') setLocAttrs(initialAsset.attributes as LocationAttributes);
      else setPropAttrs(initialAsset.attributes as PropAttributes);
    } else {
      setStep(1);
      setName(defaultName ?? '');
      setCharAttrs({});
      setLocAttrs({});
      setPropAttrs({});
      setRefImages([]);
      setCount(3);
      setSize('2K');
      setRealism(false);
      setAssetId(null);
    }
    setTaskId(null);
    setPickedCandidateId(null);
  }, [open, initialAsset, kind, defaultName]);

  const attrs = kind === 'character' ? (charAttrs as Record<string, unknown>) : kind === 'location' ? (locAttrs as Record<string, unknown>) : (propAttrs as Record<string, unknown>);
  const REALISM_HINT =
    kind === 'location'
      ? '环境写实质感(墙面/地面/家具/植被/光影自然耐看,带使用痕迹/磨损/灰尘等真实细节;不强调人物特写,弱化人脸;杜绝塑料感高光、过度对称、几何畸变、模糊招牌字符等典型 AI 痕迹,贴近实拍勘景照)'
      : kind === 'prop'
      ? '写实质感(保留材质纹理、使用痕迹、磨损/划痕/包浆等自然岁月感,杜绝塑料感高光、过度对称完美)'
      : '真人质感(保留皮肤毛孔/淡斑等自然瑕疵、发丝有断丝/碎发、皮肤光影自然,避免 AI 过度磨皮、塑料感、过度对称完美)';
  const effectiveStyle = useMemo(() => {
    if (realism) return REALISM_HINT;
    return undefined;
  }, [realism]);
  const promptPreview = useMemo(() => buildPromptPreview(kind, name, attrs), [kind, name, attrs]);

  const createMut = useMutation({
    mutationFn: async () => {
      const res = await api
        .post('brain/assets', {
          json: {
            kind,
            name,
            attributes: attrs,
            referenceImages: refImages,
          },
        })
        .json<BrainAsset>();
      return res;
    },
  });

  const generateMut = useMutation({
    mutationFn: async (aid: string) => {
      const res = await api
        .post(`brain/assets/${aid}/generate`, {
          json: { count, size, style: effectiveStyle, referenceOverride: refImages },
        })
        .json<BrainGenTask>();
      return res;
    },
  });

  const lockMut = useMutation({
    mutationFn: async (vars: { aid: string; tid: string; cid: string }) => {
      const res = await api
        .post(`brain/assets/${vars.aid}/lock`, {
          json: { taskId: vars.tid, candidateId: vars.cid },
        })
        .json<BrainAsset>();
      return res;
    },
  });

  const handleStart = async () => {
    if (!name.trim()) {
      toast.error('请填写名称');
      return;
    }
    try {
      let aid = assetId;
      if (!aid) {
        const created = await createMut.mutateAsync();
        aid = created.assetId;
        setAssetId(aid);
        onCreated?.(created);
      } else {
        // 已有 asset 时,先把维度/参考图同步上去(支持 wizard 内编辑)
        await api
          .patch(`brain/assets/${aid}`, {
            json: { name, attributes: attrs, referenceImages: refImages },
          })
          .json();
      }
      const t = await generateMut.mutateAsync(aid!);
      setTaskId(t.taskId);
      setPickedCandidateId(null);
    } catch (e) {
      toast.error('开始生成失败', (e as Error).message || '网络异常');
    }
  };

  const handleRegenerate = () => {
    // 清掉当前任务,设置区重新可编辑
    setTaskId(null);
    setPickedCandidateId(null);
  };

  const handleLock = async () => {
    if (!assetId || !taskId || !pickedCandidateId) return;
    try {
      await lockMut.mutateAsync({ aid: assetId, tid: taskId, cid: pickedCandidateId });
      toast.success('已锁定设定图');
      // 触发抽屉/列表刷新(角色设定页面立即反映新锁定图)
      qc.invalidateQueries({ queryKey: ['brain', 'asset', assetId] });
      qc.invalidateQueries({ queryKey: ['brain', 'assets'] });
      onCreated?.({ ...(initialAsset ?? ({} as BrainAsset)), assetId } as BrainAsset);
      onClose();
    } catch (e) {
      toast.error('锁定失败', (e as Error).message || '稍后重试');
    }
  };

  const titleByStep: Record<Step, string> = {
    1: `新建${label} · 基本信息`,
    2: `新建${label} · 参考图`,
    3: `${label}「${name || '未命名'}」 · 出图设置与多视图生成`,
  };

  // step 3 同屏显示任务进度,把轮询挪到 Dialog 层方便控制 footer
  const { data: task } = useQuery({
    queryKey: ['brain', 'task', taskId],
    enabled: !!taskId,
    queryFn: () => api.get(`brain/tasks/${taskId}`).json<BrainGenTask>(),
    refetchInterval: (q) => {
      const t = q.state.data as BrainGenTask | undefined;
      if (!t) return 2_000;
      return t.status === 'running' || t.status === 'queued' ? 5_000 : false;
    },
  });

  const taskRunning = task && (task.status === 'queued' || task.status === 'running');
  const settingsLocked = !!taskId; // 一旦发起任务就锁定设置区

  const footerByStep = () => {
    if (step === 1) {
      return (
        <>
          <Button variant="ghost" onClick={onClose}>取消</Button>
          <Button onClick={() => setStep(2)} disabled={!name.trim()}>
            下一步:参考图 <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <Button variant="ghost" onClick={() => setStep(1)}>
            <ChevronLeft className="h-4 w-4" /> 上一步
          </Button>
          <Button onClick={() => setStep(3)}>
            {refImages.length === 0 ? '跳过参考图,继续' : '下一步:出图设置'}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      );
    }
    // step === 3:三态
    const leftBtn = !initialAsset && !taskId ? (
      <Button variant="ghost" onClick={() => setStep(2)}>
        <ChevronLeft className="h-4 w-4" /> 上一步
      </Button>
    ) : (
      <Button variant="ghost" onClick={onClose}>取消</Button>
    );
    if (!taskId) {
      return (
        <>
          {leftBtn}
          <Button
            variant="agent"
            agentColor="brain"
            onClick={handleStart}
            disabled={createMut.isPending || generateMut.isPending || !name.trim()}
          >
            <Wand2 className="h-4 w-4" />
            开始生成 {count} 张
          </Button>
        </>
      );
    }
    if (taskRunning) {
      return (
        <>
          {leftBtn}
          <Button variant="agent" agentColor="brain" disabled>
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            生成中…
          </Button>
        </>
      );
    }
    // taskDone
    return (
      <>
        <Button variant="ghost" onClick={handleRegenerate}>
          <RefreshCw className="h-3.5 w-3.5" />
          重新生成
        </Button>
        <Button
          variant="agent"
          agentColor="brain"
          disabled={!pickedCandidateId || lockMut.isPending}
          onClick={handleLock}
        >
          <Lock className="h-3.5 w-3.5" />
          锁定选中
        </Button>
      </>
    );
  };

  return (
    <>
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o && lightboxUrl) return;
        if (!o) onClose();
      }}
      title={titleByStep[step]}
      description={
        step === 1
          ? '填写名称并定义维度,空字段不会进入提示词。'
          : step === 2
          ? '参考图仅作为团队内的视觉资料管理,不会影响 AI 出图(避免人脸/服装泄漏)。'
          : !taskId
          ? '选择数量 / 质量 / 风格(后端将并发调用 Seedream,约 30-90 秒)。点击开始生成后设置区将锁定。'
          : taskRunning
          ? 'AI 正在生成,设置已锁定。完成后可在下方选一张锁定为设定图。'
          : '生成已完成。点「重新生成」可改设置后再来一轮;选一张后点「锁定选中」。'
      }
      className="max-w-3xl"
      footer={footerByStep()}
    >
      {step === 1 && (
        <StepBasic
          kind={kind}
          name={name}
          onName={setName}
          charAttrs={charAttrs}
          onCharAttrs={setCharAttrs}
          locAttrs={locAttrs}
          onLocAttrs={setLocAttrs}
          propAttrs={propAttrs}
          onPropAttrs={setPropAttrs}
        />
      )}
      {step === 2 && (
        <StepReference
          kind={kind}
          refImages={refImages}
          onRefImages={setRefImages}
        />
      )}
      {step === 3 && (
        <div className="space-y-5">
          <StepGenerate
            kind={kind}
            count={count}
            onCount={setCount}
            size={size}
            onSize={setSize}
            realism={realism}
            onRealism={setRealism}
            promptPreview={promptPreview}
            effectiveStyle={effectiveStyle}
            disabled={settingsLocked}
          />
          {taskId && (
            <>
              <div className="h-px w-full bg-outline-variant" />
              <StepResult
                task={task ?? null}
                pickedId={pickedCandidateId}
                onPick={setPickedCandidateId}
                onViewLarge={setLightboxUrl}
              />
            </>
          )}
        </div>
      )}
    </Modal>

    <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </>
  );
}

// ---------- Step 1 ----------

function StepBasic(props: {
  kind: WizardKind;
  name: string;
  onName: (v: string) => void;
  charAttrs: CharacterAttributes;
  onCharAttrs: (v: CharacterAttributes) => void;
  locAttrs: LocationAttributes;
  onLocAttrs: (v: LocationAttributes) => void;
  propAttrs: PropAttributes;
  onPropAttrs: (v: PropAttributes) => void;
}) {
  const { kind, name, onName, charAttrs, onCharAttrs, locAttrs, onLocAttrs, propAttrs, onPropAttrs } = props;
  const { data: VIDEO_STYLES = [] } = useVideoStyles();
  const styleNames = VIDEO_STYLES.map((s) => s.name);
  return (
    <div className="space-y-4">
      <Field label="名称" required>
        <Input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder={kind === 'character' ? '如:林初夏' : kind === 'location' ? '如:总裁办公室' : '如:寒霜剑'}
        />
      </Field>

      {kind === 'character' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="性别">
            <ChipSelect
              options={['女性', '男性', '其它']}
              value={charAttrs.gender}
              onChange={(v) => onCharAttrs({ ...charAttrs, gender: v })}
            />
          </Field>
          <Field label="年龄段">
            <ChipSelect
              options={AGE_RANGE_OPTIONS}
              value={charAttrs.age}
              onChange={(v) => onCharAttrs({ ...charAttrs, age: v })}
            />
          </Field>
          <Field label="角色定位">
            <ChipSelect
              options={CHARACTER_ROLES}
              value={charAttrs.role}
              onChange={(v) => onCharAttrs({ ...charAttrs, role: v })}
            />
          </Field>
          <Field label="影视风格">
            <ChipSelect
              options={styleNames}
              value={charAttrs.filmStyle}
              onChange={(v) => onCharAttrs({ ...charAttrs, filmStyle: v })}
            />
            {charAttrs.filmStyle && (() => {
              const meta = VIDEO_STYLES.find((s) => s.name === charAttrs.filmStyle);
              if (!meta) return null;
              return (
                <div className="mt-1 rounded bg-surface-variant/30 px-2 py-1 text-[11px] text-neutral-600">
                  <span className="font-medium">{meta.tagline}</span>
                  <span className="ml-1.5 text-neutral-400">适合: {meta.suitableFor.join('、')}</span>
                </div>
              );
            })()}
          </Field>
          <Field label="性格(多选)" className="sm:col-span-2">
            <ChipMulti
              options={CHARACTER_PERSONALITY_OPTIONS}
              value={charAttrs.personality ?? []}
              onChange={(v) => onCharAttrs({ ...charAttrs, personality: v })}
            />
          </Field>
          <Field label="喜好 / 习惯" className="sm:col-span-2">
            <Input
              value={charAttrs.preferences ?? ''}
              placeholder="如:爱穿正装、随身带笔记本、习惯独自喝咖啡"
              onChange={(e) => onCharAttrs({ ...charAttrs, preferences: e.target.value })}
            />
          </Field>
          <Field label="外貌补充(可选)" className="sm:col-span-2">
            <Input
              value={charAttrs.appearance ?? ''}
              placeholder="发色、瞳色、身高、特殊配饰等"
              onChange={(e) => onCharAttrs({ ...charAttrs, appearance: e.target.value })}
            />
          </Field>
          <Field label="其它说明(可选)" className="sm:col-span-2">
            <TextArea
              value={charAttrs.extra ?? ''}
              placeholder="任何想强调的细节,如人设关键道具、性格反差等"
              onChange={(v) => onCharAttrs({ ...charAttrs, extra: v })}
            />
          </Field>

          <details className="rounded-lg border border-outline-variant/40 bg-surface-variant/20 p-3">
            <summary className="cursor-pointer select-none text-xs font-medium text-neutral-600">
              深层人设（可选）· 展开填写更丰富的角色画像，提升设定图与人物气质的匹配度
            </summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="深层渴望" className="sm:col-span-2">
                <TextArea
                  value={charAttrs.desire ?? ''}
                  placeholder="如:表面争夺遗产,内心渴望被这个家认可"
                  onChange={(v) => onCharAttrs({ ...charAttrs, desire: v })}
                />
              </Field>
              <Field label="核心恐惧" className="sm:col-span-2">
                <TextArea
                  value={charAttrs.fear ?? ''}
                  placeholder="如:害怕有一天发现自己和最讨厌的人一样"
                  onChange={(v) => onCharAttrs({ ...charAttrs, fear: v })}
                />
              </Field>
              <Field label="核心缺陷" className="sm:col-span-2">
                <TextArea
                  value={charAttrs.flaw ?? ''}
                  placeholder="如:用自我牺牲回避真实情感,导致错失真心"
                  onChange={(v) => onCharAttrs({ ...charAttrs, flaw: v })}
                />
              </Field>
              <Field label="最有意思的矛盾" className="sm:col-span-2">
                <TextArea
                  value={charAttrs.contradiction ?? ''}
                  placeholder="如:极度渴望被爱,却总用强硬推开关心她的人"
                  onChange={(v) => onCharAttrs({ ...charAttrs, contradiction: v })}
                />
              </Field>
              <Field label="标志性行为" className="sm:col-span-2">
                <Input
                  value={charAttrs.signatureBehavior ?? ''}
                  placeholder="如:紧张时整理桌面;说谎时摸耳垂;生气了反而笑"
                  onChange={(e) => onCharAttrs({ ...charAttrs, signatureBehavior: e.target.value })}
                />
              </Field>
              <Field label="说话潜台词" className="sm:col-span-2">
                <Input
                  value={charAttrs.subtext ?? ''}
                  placeholder="如:说「无所谓」时最在乎;越关心越用刻薄掩盖"
                  onChange={(e) => onCharAttrs({ ...charAttrs, subtext: e.target.value })}
                />
              </Field>
              <Field label="语言风格" className="sm:col-span-2">
                <Input
                  value={charAttrs.speakingStyle ?? ''}
                  placeholder="如:短句利落,喜用反问,绝不直接示弱"
                  onChange={(e) => onCharAttrs({ ...charAttrs, speakingStyle: e.target.value })}
                />
              </Field>
              <Field label="人物关系" className="sm:col-span-2">
                <TextArea
                  value={charAttrs.relations ?? ''}
                  placeholder="如:与反派表面盟友暗中博弈;与男主先敌后友,第15集后信任转折"
                  onChange={(v) => onCharAttrs({ ...charAttrs, relations: v })}
                />
              </Field>
            </div>
          </details>
        </div>
      ) : kind === 'location' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="场景类型">
            <ChipSelect
              options={LOCATION_KIND_OPTIONS}
              value={locAttrs.kind}
              onChange={(v) => onLocAttrs({ ...locAttrs, kind: v })}
            />
          </Field>
          <Field label="视觉风格">
            <ChipSelect
              options={styleNames}
              value={locAttrs.visualStyle}
              onChange={(v) => onLocAttrs({ ...locAttrs, visualStyle: v })}
            />
            {locAttrs.visualStyle && (() => {
              const meta = VIDEO_STYLES.find((s) => s.name === locAttrs.visualStyle);
              if (!meta) return null;
              return (
                <div className="mt-1 rounded bg-surface-variant/30 px-2 py-1 text-[11px] text-neutral-600">
                  <span className="font-medium">{meta.tagline}</span>
                  <span className="ml-1.5 text-neutral-400">适合: {meta.suitableFor.join('、')}</span>
                </div>
              );
            })()}
          </Field>
          <Field label="氛围">
            <Input
              value={locAttrs.atmosphere ?? ''}
              placeholder="如:温暖、压抑、神秘"
              onChange={(e) => onLocAttrs({ ...locAttrs, atmosphere: e.target.value })}
            />
          </Field>
          <Field label="时间段">
            <Input
              value={locAttrs.time ?? ''}
              placeholder="如:黄昏、深夜、清晨"
              onChange={(e) => onLocAttrs({ ...locAttrs, time: e.target.value })}
            />
          </Field>
          <Field label="灯光风格">
            <Input
              value={locAttrs.lighting ?? ''}
              placeholder="如:逆光暖调、霓虹冷调、自然柔光"
              onChange={(e) => onLocAttrs({ ...locAttrs, lighting: e.target.value })}
            />
          </Field>
          <Field label="关键元素">
            <Input
              value={(locAttrs.keyElements ?? []).join('、')}
              placeholder="逗号或顿号分隔,如:落地窗、皮质沙发、雕花屏风"
              onChange={(e) =>
                onLocAttrs({
                  ...locAttrs,
                  keyElements: e.target.value
                    .split(/[,,、]/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </Field>
          <Field label="其它说明(可选)" className="sm:col-span-2">
            <TextArea
              value={locAttrs.extra ?? ''}
              placeholder="任何场景特色,如标志性符号、材质感、空间结构等"
              onChange={(v) => onLocAttrs({ ...locAttrs, extra: v })}
            />
          </Field>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="道具类别">
            <ChipSelect
              options={PROP_CATEGORY_OPTIONS}
              value={propAttrs.category}
              onChange={(v) => onPropAttrs({ ...propAttrs, category: v })}
            />
          </Field>
          <Field label="材质">
            <ChipSelect
              options={PROP_MATERIAL_OPTIONS}
              value={propAttrs.material}
              onChange={(v) => onPropAttrs({ ...propAttrs, material: v })}
            />
          </Field>
          <Field label="风格">
            <ChipSelect
              options={PROP_STYLE_OPTIONS}
              value={propAttrs.style}
              onChange={(v) => onPropAttrs({ ...propAttrs, style: v })}
            />
          </Field>
          <Field label="尺寸">
            <ChipSelect
              options={PROP_SIZE_OPTIONS}
              value={propAttrs.size}
              onChange={(v) => onPropAttrs({ ...propAttrs, size: v })}
            />
          </Field>
          <Field label="影视风格">
            <ChipSelect
              options={styleNames}
              value={propAttrs.visualStyle}
              onChange={(v) => onPropAttrs({ ...propAttrs, visualStyle: v })}
            />
            {propAttrs.visualStyle && (() => {
              const meta = VIDEO_STYLES.find((s) => s.name === propAttrs.visualStyle);
              if (!meta) return null;
              return (
                <div className="mt-1 rounded bg-surface-variant/30 px-2 py-1 text-[11px] text-neutral-600">
                  <span className="font-medium">{meta.tagline}</span>
                  <span className="ml-1.5 text-neutral-400">适合: {meta.suitableFor.join('、')}</span>
                </div>
              );
            })()}
          </Field>
          <Field label="当前形象名称" className="sm:col-span-2">
            <Input
              value={propAttrs.variantName ?? ''}
              placeholder="如:觉醒形态、冰霜附魔、基础款"
              onChange={(e) => onPropAttrs({ ...propAttrs, variantName: e.target.value })}
            />
          </Field>
          <Field label="形象描述" className="sm:col-span-2">
            <TextArea
              value={propAttrs.variantDesc ?? ''}
              placeholder="描述此形象的视觉特征,如:剑身散发淡蓝寒光,剑格镶嵌冰晶宝石,握柄缠绕霜纹"
              onChange={(v) => onPropAttrs({ ...propAttrs, variantDesc: v })}
            />
          </Field>
          <Field label="外观补充(可选)" className="sm:col-span-2">
            <Input
              value={propAttrs.appearance ?? ''}
              placeholder="如:长度、颜色、特殊纹理、开关/变形状态等"
              onChange={(e) => onPropAttrs({ ...propAttrs, appearance: e.target.value })}
            />
          </Field>
          <Field label="其它说明(可选)" className="sm:col-span-2">
            <TextArea
              value={propAttrs.extra ?? ''}
              placeholder="任何想在设定图中强调的细节"
              onChange={(v) => onPropAttrs({ ...propAttrs, extra: v })}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

// ---------- Step 2 ----------

function StepReference(props: {
  kind: WizardKind;
  refImages: string[];
  onRefImages: (v: string[]) => void;
}) {
  const { kind, refImages, onRefImages } = props;
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { data: templates } = useQuery({
    queryKey: ['brain', 'reference-templates', kind],
    queryFn: () =>
      api
        .get('brain/reference/templates', { searchParams: { kind, limit: 30 } })
        .json<BrainTemplateListResponse>(),
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('brain/reference/upload', { body: fd }).json<{ url: string }>();
      return res;
    },
    onSuccess: (res) => {
      onRefImages([...refImages, res.url]);
      toast.success('参考图已上传');
    },
    onError: (e: Error) => toast.error('上传失败', e.message || '网络异常'),
  });

  const handlePick = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) => uploadMut.mutate(f));
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-2 text-sm font-medium">已选参考图</div>
        {refImages.length === 0 ? (
          <div className="rounded-md border border-dashed border-outline-variant p-6 text-center text-xs text-neutral-500">
            未选参考图。你可以「上传新图」或「从历史挑选」,也可以直接跳过(模型仅按维度生成)。
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {refImages.map((url) => (
              <div key={url} className="relative h-20 w-20 overflow-hidden rounded border border-outline-variant">
                <img src={url} alt="ref" className="h-full w-full object-cover" />
                <button
                  type="button"
                  className="absolute right-0 top-0 rounded-bl bg-black/60 p-0.5 text-white hover:bg-black/80"
                  onClick={() => onRefImages(refImages.filter((u) => u !== url))}
                  aria-label="移除"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploadMut.isPending}
        >
          <Upload className="h-3.5 w-3.5" />
          {uploadMut.isPending ? '上传中…' : '上传新参考图'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handlePick(e.target.files)}
        />
      </div>

      <div>
        <div className="mb-2 text-sm font-medium">从历史模板挑选</div>
        {(templates?.items ?? []).length === 0 ? (
          <div className="text-xs text-neutral-500">还没有历史参考图。</div>
        ) : (
          <div className="grid grid-cols-6 gap-2">
            {templates!.items.map((t) => {
              const picked = refImages.includes(t.url);
              return (
                <button
                  key={t.url}
                  type="button"
                  className={cn(
                    'relative h-20 w-full overflow-hidden rounded border-2 transition',
                    picked ? 'border-brand-500' : 'border-transparent hover:border-outline-variant',
                  )}
                  title={t.assetName ?? ''}
                  onClick={() =>
                    onRefImages(picked ? refImages.filter((u) => u !== t.url) : [...refImages, t.url])
                  }
                >
                  <img src={t.url} alt="" className="h-full w-full object-cover" />
                  {picked && (
                    <div className="absolute right-1 top-1 rounded-full bg-brand-500 p-0.5 text-white">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Step 3 ----------

function StepGenerate(props: {
  kind: WizardKind;
  count: number;
  onCount: (v: number) => void;
  size: string;
  onSize: (v: string) => void;
  realism: boolean;
  onRealism: (v: boolean) => void;
  promptPreview: string;
  effectiveStyle: string | undefined;
  disabled?: boolean;
}) {
  const { kind, count, onCount, size, onSize, realism, onRealism, promptPreview, effectiveStyle, disabled } = props;
  const isLocation = kind === 'location';
  const isProp = kind === 'prop';
  const realismLabel = isLocation ? '环境真实感增强' : isProp ? '材质真实感增强' : '真人感增强';
  const realismToggleText = isLocation
    ? (realism ? '已开启 · 强化环境真实感' : '已关闭 · 强化环境真实感')
    : isProp
    ? (realism ? '已开启 · 强化材质纹理与岁月感' : '已关闭 · 强化材质纹理与岁月感')
    : (realism ? '已开启 · 提升真人质感' : '已关闭 · 提升真人质感');
  const realismDesc = isLocation
    ? '开启后会在提示词里强调:墙面/家具/光影/使用痕迹等环境细节自然耐看,弱化人物存在,杜绝塑料感、过度对称等典型 AI 痕迹,贴近实拍勘景照。'
    : isProp
    ? '开启后会在提示词里强调:材质纹理写实(金属拉丝/木质年轮/布料经纬)、使用痕迹(磨损/划痕/包浆)、自然光影反射,杜绝塑料感高光、过度对称完美,贴近实物拍摄。'
    : '开启后会在提示词里强调:保留皮肤毛孔与瑕疵、自然光影、碎发,削弱 AI 过度磨皮的塑料感。';
  return (
    <div
      className={cn('space-y-5', disabled && 'pointer-events-none opacity-60')}
      aria-disabled={disabled}
    >
      <div>
        <div className="mb-2 text-sm font-medium">本轮出图数量</div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={1}
            max={6}
            value={count}
            disabled={disabled}
            onChange={(e) => onCount(Number(e.target.value))}
            className="flex-1 accent-brand-500"
          />
          <Badge variant="info" className="min-w-[40px] justify-center">
            {count} 张
          </Badge>
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          后端将以 asyncio.gather 并发调用 Seedream;典型耗时 30 - 90 秒。
        </p>
      </div>

      <div>
        <div className="mb-2 text-sm font-medium">图片质量</div>
        <div className="flex flex-wrap gap-1.5">
          {SEEDREAM_QUALITY_OPTIONS.map((o) => {
            const active = size === o.value;
            return (
              <button
                key={o.value}
                type="button"
                title={o.desc}
                disabled={disabled}
                onClick={() => onSize(o.value)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition',
                  active
                    ? 'border-brand-500 bg-brand-500/10 text-brand-500'
                    : 'border-outline-variant text-neutral-600 hover:border-outline',
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          {SEEDREAM_QUALITY_OPTIONS.find((o) => o.value === size)?.desc ?? ''}
        </p>
      </div>

      {/* 真实感增强开关 · 角色 / 场景 / 道具均可用 */}
      {(isLocation || isProp || kind === 'character') && (
        <div>
          <div className="mb-2 text-sm font-medium">{realismLabel}</div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onRealism(!realism)}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition',
              realism
                ? 'border-brand-500 bg-brand-500/10 text-brand-500'
                : 'border-outline-variant text-neutral-600 hover:border-outline',
            )}
          >
            <span
              className={cn(
                'inline-flex h-3.5 w-7 items-center rounded-full border transition',
                realism ? 'border-brand-500 bg-brand-500' : 'border-outline-variant bg-surface-variant',
              )}
            >
              <span
                className={cn(
                  'inline-block h-2.5 w-2.5 rounded-full bg-white shadow transition-transform',
                  realism ? 'translate-x-3.5' : 'translate-x-0.5',
                )}
              />
            </span>
            {realismToggleText}
          </button>
          <p className="mt-1 text-xs text-neutral-500">{realismDesc}</p>
        </div>
      )}

      <details className="rounded-md border border-outline-variant bg-surface-variant/30 p-3 text-xs">
        <summary className="cursor-pointer select-none text-neutral-700">
          展开提示词预览(由 Agent 自动拼装,可直接生成)
        </summary>
        <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed text-neutral-700">
{effectiveStyle ? `【整体出图画风】${effectiveStyle}(贯穿所有视角,保持统一)\n\n` : ''}{promptPreview}
        </pre>
      </details>
    </div>
  );
}

// ---------- Step 4 ----------

function StepResult(props: {
  task: BrainGenTask | null;
  pickedId: string | null;
  onPick: (id: string | null) => void;
  onViewLarge: (url: string) => void;
}) {
  const { task, pickedId, onPick, onViewLarge } = props;

  if (!task) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-neutral-500">
        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        正在创建任务…
      </div>
    );
  }

  const total = task.count;
  const done = task.candidates?.length ?? 0;
  const running = task.status === 'queued' || task.status === 'running';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs">
        <div>
          {running ? (
            <span className="flex items-center gap-1.5 text-neutral-600">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              AI 正在生成 {done} / {total} 张…
            </span>
          ) : task.status === 'succeeded' ? (
            <span className="text-success">已完成 {done} 张,请选一张锁定</span>
          ) : task.status === 'partial' ? (
            <span className="text-warning">部分完成 ({done}/{total}),仍可选已成功的图</span>
          ) : (
            <span className="text-danger">生成失败:{task.error || '请重试'}</span>
          )}
        </div>
        <div className="text-outline">taskId: {task.taskId.slice(0, 12)}…</div>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded bg-surface-variant">
        <div
          className="h-full bg-brand-500 transition-all"
          style={{ width: `${Math.min(100, (done / total) * 100)}%` }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {Array.from({ length: total }).map((_, i) => {
          const cand = task.candidates?.[i];
          if (!cand) {
            return (
              <div
                key={i}
                className="flex aspect-square animate-pulse items-center justify-center rounded-md bg-surface-variant text-outline"
              >
                <ImagePlus className="h-6 w-6" />
              </div>
            );
          }
          if (cand.error) {
            return (
              <div
                key={cand.id}
                className="flex aspect-square items-center justify-center rounded-md border border-danger/30 bg-danger-soft p-2 text-center text-[11px] text-danger"
              >
                {cand.error.slice(0, 60)}
              </div>
            );
          }
          const picked = pickedId === cand.id;
          return (
            <div
              key={cand.id}
              className={cn(
                'group relative aspect-square overflow-hidden rounded-md border-2 transition',
                picked ? 'border-brand-500 ring-2 ring-brand-500/40' : 'border-transparent hover:border-outline-variant',
              )}
            >
              <button
                type="button"
                className="absolute inset-0"
                onClick={() => onPick(picked ? null : cand.id)}
              >
                <img src={cand.url} alt="" className="h-full w-full object-cover" />
              </button>
              <button
                type="button"
                className="absolute right-1 top-1 rounded-full bg-black/55 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-black/75"
                title="查看大图"
                onClick={(e) => {
                  e.stopPropagation();
                  if (cand.url) onViewLarge(cand.url);
                }}
              >
                <Maximize2 className="h-3 w-3" />
              </button>
              {picked && (
                <div className="absolute left-1 top-1 rounded-full bg-brand-500 p-1 text-white">
                  <Check className="h-3 w-3" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- 共享 UI ----------

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="text-xs font-medium text-neutral-700">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </div>
      {children}
    </div>
  );
}

function ChipSelect({
  options,
  value,
  onChange,
}: {
  options: string[];
  value?: string;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs transition',
              active
                ? 'border-brand-500 bg-brand-500/10 text-brand-500'
                : 'border-outline-variant text-neutral-600 hover:border-outline',
            )}
            onClick={() => onChange(active ? undefined : o)}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function ChipMulti({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs transition',
              active
                ? 'border-brand-500 bg-brand-500/10 text-brand-500'
                : 'border-outline-variant text-neutral-600 hover:border-outline',
            )}
            onClick={() =>
              onChange(active ? value.filter((v) => v !== o) : [...value, o])
            }
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      rows={3}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full rounded-lg border border-outline-variant bg-surface px-3 py-2 text-sm placeholder:text-outline focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
    />
  );
}

// ---------- prompt 本地预览(纯展示用,后端会重新拼装) ----------

function buildPromptPreview(kind: WizardKind, name: string, attrs: Record<string, unknown>): string {
  if (kind === 'character') {
    const a = attrs as CharacterAttributes;
    const lines = [
      '【角色设定图 · 三段式九宫格】',
      `角色名:${name || '(未填)'}`,
    ];
    if (a.gender || a.age) lines.push(`性别/年龄:${[a.gender, a.age].filter(Boolean).join(' · ')}`);
    if (a.role) lines.push(`角色定位:${a.role}`);
    if (a.personality?.length) lines.push(`性格关键词:${a.personality.join('、')}`);
    if (a.preferences) lines.push(`喜好/习惯:${a.preferences}`);
    if (a.desire) lines.push(`深层渴望:${a.desire}`);
    if (a.fear) lines.push(`核心恐惧:${a.fear}`);
    if (a.flaw) lines.push(`核心缺陷:${a.flaw}`);
    if (a.contradiction) lines.push(`人物矛盾:${a.contradiction}`);
    if (a.signatureBehavior) lines.push(`标志性行为:${a.signatureBehavior}`);
    if (a.subtext) lines.push(`说话潜台词:${a.subtext}`);
    if (a.speakingStyle) lines.push(`语言风格:${a.speakingStyle}`);
    if (a.relations) lines.push(`人物关系:${a.relations}`);
    if (a.filmStyle) lines.push(`影视风格:${a.filmStyle}`);
    if (a.appearance) lines.push(`外貌补充:${a.appearance}`);
    if (a.extra) lines.push(`其它:${a.extra}`);
    lines.push('排版:上排头部多角度 / 中排全身三视图 / 下排细节特写。同一人物、姿态比例自洽,无文字水印。');
    return lines.join('\n');
  }
  if (kind === 'prop') {
    const a = attrs as PropAttributes;
    const lines = ['【道具设定图 · 三视图】', `道具名:${name || '(未填)'}`];
    if (a.variantName) lines.push(`形象:${a.variantName}`);
    if (a.category) lines.push(`类别:${a.category}`);
    if (a.material) lines.push(`材质:${a.material}`);
    if (a.style) lines.push(`风格:${a.style}`);
    if (a.size) lines.push(`尺寸:${a.size}`);
    if (a.visualStyle) lines.push(`影视风格:${a.visualStyle}`);
    if (a.variantDesc) lines.push(`形象描述:${a.variantDesc}`);
    if (a.appearance) lines.push(`外观补充:${a.appearance}`);
    if (a.extra) lines.push(`其它:${a.extra}`);
    lines.push('排版:正视图(正面) / 侧视图(侧面) / 后视图(背面) 三视图排版,纯白/浅灰背景,道具居中,无场景环境,无人物持有,无文字水印。');
    return lines.join('\n');
  }
  const a = attrs as LocationAttributes;
  const lines = ['【场景设定图 · 多视角横版】', `场景名:${name || '(未填)'}`];
  if (a.kind) lines.push(`场景类型:${a.kind}`);
  if (a.atmosphere) lines.push(`氛围:${a.atmosphere}`);
  if (a.time) lines.push(`时间段:${a.time}`);
  if (a.lighting) lines.push(`灯光风格:${a.lighting}`);
  if (a.visualStyle) lines.push(`视觉风格:${a.visualStyle}`);
  if (a.keyElements?.length) lines.push(`关键元素:${a.keyElements.join('、')}`);
  if (a.extra) lines.push(`其它:${a.extra}`);
  lines.push('排版:主区中景全景 + 不同视角副图 + 关键元素特写;视角间空间关系自洽,无文字水印。');
  return lines.join('\n');
}
